/**
 * useFastCartTotal Hook
 *
 * NOW reads from cartStore$.fastCartTotals (no fetch).
 * Fetching is done by useCheckoutTotalsManager (called once at CheckoutScreen).
 * Return type is UNCHANGED — all existing consumers work without modification.
 *
 * @see docs/CHECKOUT-DATA-FRAGMENTATION-ANALYSIS.md
 * @module @kidslabs/module-checkout/modules/checkout/hooks/use-fast-cart-total
 */
import { useMemo } from 'react';
import { cartStoreAPI } from '@kidslabs/module-quote';
import { checkoutStore$ } from '../stores/checkout.store';
import { use$, useSelector } from '@legendapp/state/react';
import { formatPrice } from '../utils/format-price';
import { useOrderItems } from './use-order-items';

/** Default standard shipping fee (VND) — used to show savings on free shipping */
export const DEFAULT_SHIPPING_FEE = 19000;

/**
 * Loading price placeholder constant
 */
const LOADING_PRICE_PLACEHOLDER = '...';

/**
 * Hook for fast cart totals calculation
 *
 * Reads from cartStore$.fastCartTotals (populated by useCheckoutTotalsManager).
 * No Apollo queries — just reactive store reads + useMemo computations.
 *
 * @returns Object with price, basePrice, discountPrice, cart totals data, and refetchCartTotals (no-op)
 */
export const useFastCartTotal = () => {
    const fastCartTotals = useSelector(() => cartStoreAPI.state.getCartTotals()?.get());
    const loading = useSelector(() => cartStoreAPI.state.getCartTotalsLoading()?.get());
    const selectedShippingMethod = use$(checkoutStore$.selectedShippingMethod);

    const { originalTotal } = useOrderItems();

    /**
     * Grand total with loading placeholder
     */
    const grandTotal = useMemo(() => {
        return fastCartTotals?.grand_total ?? LOADING_PRICE_PLACEHOLDER;
    }, [fastCartTotals]);

    /**
     * Formatted price
     */
    const price = useMemo(() => {
        if (grandTotal === LOADING_PRICE_PLACEHOLDER) {
            return LOADING_PRICE_PLACEHOLDER;
        }
        return formatPrice({
            amount: Number(grandTotal),
            currencyCode: fastCartTotals?.currency_code ?? 'VND',
            locale: 'vi',
        });
    }, [grandTotal, fastCartTotals?.currency_code]);

    /**
     * Base price (subtotal)
     */
    const basePrice = useMemo(() => {
        const subtotal = originalTotal + (fastCartTotals?.shipping_amount ?? 0);
        if (subtotal === undefined || subtotal === null) {
            return undefined;
        }
        return formatPrice({
            amount: subtotal,
            currencyCode: fastCartTotals?.currency_code ?? 'VND',
            locale: 'vi',
        });
    }, [originalTotal, fastCartTotals?.shipping_amount, fastCartTotals?.currency_code]);

    /**
     * Discount price
     *
     * Returns undefined when fastCartTotals hasn't loaded yet to prevent
     * showing incorrect discount (originalTotal + 0 - 0 = originalTotal).
     */
    const discountPrice = useMemo(() => {
        if (!fastCartTotals) {
            return undefined;
        }
        const discountAmount =
            originalTotal + (fastCartTotals.shipping_amount ?? 0) - (fastCartTotals.grand_total ?? 0);
        if (discountAmount === 0) {
            return undefined;
        }
        return formatPrice({
            amount: discountAmount,
            currencyCode: fastCartTotals.currency_code ?? 'VND',
            locale: 'vi',
        });
    }, [originalTotal, fastCartTotals]);

    /**
     * Total products (subtotal from fast cart totals)
     */
    const totalProducts = useMemo(() => {
        const subtotal = fastCartTotals?.subtotal;
        if (subtotal === undefined || subtotal === null) {
            return undefined;
        }
        return formatPrice({
            amount: subtotal,
            currencyCode: fastCartTotals?.currency_code ?? 'VND',
            locale: 'vi',
        });
    }, [fastCartTotals?.subtotal, fastCartTotals?.currency_code]);

    /**
     * Shipping fee
     *
     * Returns undefined ONLY when server data hasn't loaded yet (null/undefined).
     * When shipping_amount is 0 (free shipping), returns formatted "0đ" so the
     * OrderItems component doesn't fall back to stale stored shipping method amount.
     */
    const shippingFee = useMemo(() => {
        const shippingAmount = fastCartTotals?.shipping_amount;
        if (shippingAmount === undefined || shippingAmount === null) {
            return undefined;
        }

        // When shipping is free (0), show the default fee as negative savings
        if (shippingAmount === 0) {
            return formatPrice({
                amount: DEFAULT_SHIPPING_FEE,
                currencyCode: fastCartTotals?.currency_code ?? 'VND',
                locale: 'vi',
            });
        }

        return formatPrice({
            amount: shippingAmount,
            currencyCode: fastCartTotals?.currency_code ?? 'VND',
            locale: 'vi',
        });
    }, [fastCartTotals?.shipping_amount, fastCartTotals?.currency_code]);

    return {
        price,
        basePrice: basePrice === price ? undefined : basePrice,
        discountPrice,
        shippingFee,
        totalProducts,
        originalTotal,
        fastCartTotalsData: fastCartTotals,
        selectedShippingMethod,
        loadingFastCartTotals: loading ?? false,
        refetchCartTotals: () => {}, // No-op — useCheckoutTotalsManager handles fetching
    };
};

export default useFastCartTotal;
